import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  RefreshCw, Package, AlertCircle, CheckCircle2, 
  Clock, Shield, XCircle, ArrowLeft, Mail, Phone 
} from 'lucide-react';

const RefundReturnPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-6 bg-white/20 hover:bg-white/30 text-white border-white/40 backdrop-blur-sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/20 backdrop-blur-md p-6 rounded-full border-2 border-white/40">
                <RefreshCw className="h-16 w-16" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-4 drop-shadow-lg">Refund & Return Policy</h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              We value your satisfaction and strive to provide clear policies for refunds and returns
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Refund Policy Section */}
        <Card className="mb-8 border-4 border-red-200 shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-r from-red-600 to-orange-500 p-3 rounded-full">
                <RefreshCw className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Refund Policy</h2>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <Clock className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">7 to 10 Days Refund Period</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We offer a refund within <span className="font-bold">7 to 10 business days</span> from the date of purchase 
                    or service activation. If you are not satisfied with our services, you may request a refund within this timeframe.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4">Eligibility for Refund:</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Service not activated or used within 7 to 10 days of purchase</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Technical issues that cannot be resolved by our support team</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Service not available in your area after confirmation</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Installation cancelled before service activation</p>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4">Non-Refundable Items:</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Services used beyond the 7-10 day refund period</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Installation charges if service has been activated</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Promotional or discounted plans (unless otherwise stated)</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Third-party service charges or equipment</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Return Device Policy Section */}
        <Card className="mb-8 border-4 border-orange-200 shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-gradient-to-r from-orange-600 to-yellow-500 p-3 rounded-full">
                <Package className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">Device Return Policy</h2>
            </div>

            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Warranty Coverage</h3>
                  <p className="text-gray-700 leading-relaxed">
                    All devices provided by Netix Fibernet come with a manufacturer's warranty. 
                    If your device is under warranty, we will repair or replace it at no additional cost.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4">What is Covered Under Warranty:</h3>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Manufacturing defects</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Hardware malfunctions during normal use</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Software issues related to the device firmware</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700">Components failure under normal operating conditions</p>
              </div>
            </div>

            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Physical Damage NOT Covered</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Please note that <span className="font-bold">physical damage is NOT covered</span> under warranty. 
                    This includes drops, water damage, fire damage, or any other external physical harm to the device.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-4">What is NOT Covered:</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><span className="font-bold">Physical/Accidental Damage:</span> Drops, cracks, water damage, fire, or impact damage</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><span className="font-bold">Misuse or Abuse:</span> Damage caused by improper handling or unauthorized modifications</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><span className="font-bold">Unauthorized Repairs:</span> Devices repaired by non-authorized service centers</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><span className="font-bold">Normal Wear and Tear:</span> Cosmetic issues that do not affect functionality</p>
              </div>
              <div className="flex items-start gap-3">
                <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><span className="font-bold">Electrical Surges:</span> Damage due to power surges or incorrect voltage supply</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How to Request Section */}
        <Card className="mb-8 border-4 border-yellow-200 shadow-xl">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Request a Refund or Return</h2>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border-2 border-blue-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Step 1: Contact Customer Support</h3>
                <p className="text-gray-700 mb-3">
                  Reach out to our customer support team via phone or email within the refund/return period.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-red-600" />
                    <a href="tel:+919319919988" data-testid="refund-support-mobile-link" className="text-red-600 font-semibold hover:underline">
                      +91 93199 19988
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-red-600" />
                    <a href="tel:06276459015" data-testid="refund-office-phone-link" className="text-red-600 font-semibold hover:underline">
                      Office Phone - 06276 - 459015
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-red-600" />
                    <a href="mailto:care@netixfibernet.com" className="text-red-600 font-semibold hover:underline">
                      care@netixfibernet.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Step 2: Provide Required Information</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">•</span>
                    <span>Customer ID or Invoice Number</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">•</span>
                    <span>Reason for refund or device return</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">•</span>
                    <span>Date of purchase or service activation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold text-purple-600">•</span>
                    <span>Device serial number (for device returns)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Step 3: Await Approval</h3>
                <p className="text-gray-700">
                  Our team will review your request and notify you of the approval status within 2-3 business days. 
                  Approved refunds will be processed to your original payment method within 7-10 business days.
                </p>
              </div>

              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border-2 border-orange-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Step 4: Return Device (if applicable)</h3>
                <p className="text-gray-700">
                  For device returns, our technician will collect the faulty device from your location at no extra charge. 
                  Please ensure the device is in its original condition with all accessories included.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Important Notes */}
        <Card className="mb-8 border-4 border-gray-300 shadow-xl">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <AlertCircle className="h-8 w-8 text-gray-600" />
              <h2 className="text-3xl font-bold text-gray-900">Important Notes</h2>
            </div>
            
            <div className="space-y-3 text-gray-700">
              <div className="flex items-start gap-3">
                <span className="text-2xl font-bold text-gray-400">•</span>
                <p>All refund requests must be submitted within 7 to 10 days from the date of purchase or service activation.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl font-bold text-gray-400">•</span>
                <p>Refund processing time may vary depending on your payment method and bank.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl font-bold text-gray-400">•</span>
                <p>For device returns under warranty, please do not attempt to repair the device yourself as it may void the warranty.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl font-bold text-gray-400">•</span>
                <p>Physical damage to devices will not be covered under warranty and may incur repair charges.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl font-bold text-gray-400">•</span>
                <p>Netix Fibernet reserves the right to refuse refunds or returns if the terms and conditions are not met.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 rounded-2xl p-8 text-center text-white shadow-2xl">
          <h2 className="text-3xl font-bold mb-4">Have Questions?</h2>
          <p className="text-lg mb-6 text-white/90">
            Our customer support team is here to help you with any queries regarding refunds or returns.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => window.location.href = 'tel:+919319919988'}
              data-testid="refund-call-mobile-button"
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-100 font-bold text-lg px-8"
            >
              <Phone className="h-5 w-5 mr-2" />
              Call Us Now
            </Button>
            <Button
              onClick={() => window.location.href = 'tel:06276459015'}
              data-testid="refund-call-office-button"
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/20 font-bold text-lg px-8"
            >
              <Phone className="h-5 w-5 mr-2" />
              Office Phone
            </Button>
            <Button
              onClick={() => window.location.href = 'mailto:care@netixfibernet.com'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/20 font-bold text-lg px-8"
            >
              <Mail className="h-5 w-5 mr-2" />
              Email Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundReturnPolicy;
